/**
 * Chat API + SSE client (owned by the chat UI). Every endpoint, tool and stream event the chat surfaces use
 * (shapes as implemented in apps/server/src/http/routes-chat.ts + routes-api.ts; the client is tolerant of drift):
 *
 * Threads
 *   GET    /api/chat/threads                    → { threads: ChatThread[], unreadCount }
 *   POST   /api/chat/threads                    { brandId?, title?, autonomy?, mode?, thinkingLevel?, context? } → 201 { thread }
 *   GET    /api/chat/threads/:id                → { thread: ChatThread, messages: ChatMessage[] }
 *   PATCH  /api/chat/threads/:id                { title?, autonomy?, mode?, thinkingLevel? } → { thread }   (404/405 → kept locally)
 *   POST   /api/chat/threads/:id/read           → { ok }   (failures ignored)
 *   POST   /api/chat/threads/:id/stop           → { stopping, status }   (the live stream then emits `stopped`)
 *   GET    /api/chat/threads/:id/events         → text/event-stream   (re-attach to a live run after reload/navigation)
 *   GET    /api/chat/templates                  → { groups, autonomyModes, autonomyNote, modes, thinkingLevels, contextBudget, agentName }
 *
 * Messages
 *   POST   /api/chat/threads/:id/messages       { content, composer?: { mode: "instant"|"thinking", thinkingLevel?, autonomy?,
 *          imageModel?, videoModel?, videoResolution? }, attachments?: [{ kind: "upload"|"asset"|"generated"|"creation"|
 *          "inspiration", id?, storageKey?, name?, url?, mediaType? }] }
 *          → text/event-stream for a new run; 202 { queued: true, inputId } while a run is active (interjection,
 *            applied at the next turn boundary → `interjection_applied`).
 *   POST   /api/chat/threads/:id/answers        { attachmentId, cancelled?, answers: [{ questionId, optionIds?, text?,
 *          files?: [{ storageKey } | { assetId } | { creativeId }], skipped? }] } → text/event-stream continuing the run
 *   POST   /api/chat/messages/:id/feedback      { rating: "up"|"down", note? } → { feedback }
 *   POST   /api/uploads (multipart, via client.uploadFile purpose "chat") → { key, url, contentType, bytes, kind, name }
 *
 * Reports (artifacts)
 *   GET    /api/artifacts/:id                    → { artifact: Artifact }   (fallback: tool `reports.get { artifactId }`)
 *   tool   reports.list { brandId?, threadId?, documentId? } → { artifacts | reports | items: Artifact[] }   (‹ › navigation)
 *
 * Tools used by chat cards (POST /api/tools/:name): creatives.models { brandId } → { defaults, image[], video[] },
 *   creatives.list { brandId, source?, limit }, creatives.get, creatives.approve / creatives.reject (ApprovalActions),
 *   creatives.approve_script { brandId, creativeId }, creatives.request_script_changes { brandId, creativeId, note },
 *   references.list { brandId, saved: true }, pending.approve { pendingActionId }, pending.reject { pendingActionId, reason? },
 *   recommendation `tool`s via runTool (two-step confirmation flow).
 *
 * Stream events — SSE `event:` name or a `type` field in the JSON data; unknown events are ignored:
 *   status               { status: "running" | "idle" | "awaiting_user" | "error", label?, runId?, messageId? }
 *                        (label = "Mimic is thinking…", "Analyzing image…", per-tool labels)
 *   text|delta|text_delta { delta | text }
 *   thinking             { delta? | text? }
 *   tool_call|tool_use   { id, name, input }
 *   tool_result          { id, name, ok, isError, preview }   (also tolerates result|content|output|error)
 *   attachment           { messageId, attachment: ChatAttachment }
 *   question             { messageId, attachment: { kind: "question", id, data: { title, questions, status } } }   (run pauses)
 *   message              { message } | ChatMessage   (a persisted row; optional)
 *   usage                { inputTokens, outputTokens, contextTokens, contextBudget, pctFull }
 *   interjection_queued  { inputId, text }
 *   interjection_applied { inputIds, count }
 *   stopped { threadId } · done { threadId, contextTokens } · error { error, message }
 */
import { api, ApiError, callTool } from "./client";
import { pickArray, pickObject } from "./types";
import { lastBrand } from "@/state/brand";

/** Capture selection in the URL when a request starts (never a session-wide server setting). */
export function chatBrandPath(path: string, brandId: string | null = lastBrand()): string {
  if (!brandId) throw new ApiError(400, "brand_required", "Select a brand to use chat.");
  return `${path}${path.includes("?") ? "&" : "?"}brandId=${encodeURIComponent(brandId)}`;
}

// ─── Threads & messages ────────────────────────────────────────────────────

export type Autonomy = "confirm" | "auto_ask" | "auto";
export type ThreadStatus = "idle" | "running" | "awaiting_user" | "error";

export interface ChatThread {
  id: string;
  title: string;
  brandId?: string | null;
  /** "chat" (interactive) | "scheduled" (posted to by a schedule). */
  kind?: "chat" | "scheduled" | string;
  autonomy?: Autonomy | string;
  model?: string;
  /** Input tokens of the last agent turn (context-window meter). */
  contextTokens?: number;
  contextWindow?: number;
  status?: ThreadStatus | string;
  pendingQuestion?: Record<string, unknown> | null;
  sourceRef?: string | null;
  createdAt?: string;
  updatedAt?: string;
  lastMessageAt?: string | null;
  lastReadAt?: string | null;
  unread?: boolean;
  scheduleId?: string | null;
  lastMessage?: string | null;
  /** "instant" | "thinking". */
  mode?: string;
  thinkingLevel?: string;
  /** Messages queued while a run is active (applied at the next turn boundary). */
  pendingInputs?: { id: string; text: string; at?: string }[];
}

/**
 * question = interactive ask_user card; answers = the human's submitted answers; artifact = report card
 * ({artifactId, documentId, revision, title, kind}); context = data the human attached.
 */
export interface ChatAttachment {
  kind:
    | "creative"
    | "asset"
    | "image"
    | "video"
    | "approval_request"
    | "confirmation"
    | "recommendation"
    | "table"
    | "question"
    | "answers"
    | "artifact"
    | "context"
    | string;
  id?: string;
  url?: string;
  data?: Record<string, unknown>;
}

export type ContentBlock =
  | { type: "text"; text: string }
  | { type: "tool_use"; id: string; name: string; input: unknown }
  | { type: "tool_result"; tool_use_id: string; content?: unknown; is_error?: boolean }
  | { type: string; [k: string]: unknown };

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: ContentBlock[];
  attachments?: ChatAttachment[];
  createdAt?: string;
  seq?: number;
  usage?: Record<string, unknown> | null;
  meta?: Record<string, unknown>;
  /** This user's 👍/👎 on an agent message. */
  myFeedback?: "up" | "down" | null;
}

const DEFAULT_CONTEXT_WINDOW = 200_000;

function isoOrNull(v: unknown): string | null {
  if (typeof v === "string" && v) return v;
  if (v instanceof Date) return v.toISOString();
  return null;
}

/** Tolerant thread normaliser — derives `unread` and `scheduleId` (rail flyout) when the server doesn't. */
export function normalizeThread(raw: unknown): ChatThread | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.id !== "string") return null;
  const lastMessageAt = isoOrNull(t.lastMessageAt);
  const lastReadAt = isoOrNull(t.lastReadAt);
  // Only derive unread when the server tracks read state (lastReadAt present, even if null).
  const unread =
    typeof t.unread === "boolean"
      ? t.unread
      : "lastReadAt" in t && !!lastMessageAt && (!lastReadAt || new Date(lastMessageAt).getTime() > new Date(lastReadAt).getTime() + 1000);
  const kind = typeof t.kind === "string" ? t.kind : "chat";
  return {
    ...(t as unknown as ChatThread),
    title: typeof t.title === "string" ? t.title : "New chat",
    kind,
    unread,
    lastMessageAt,
    lastReadAt,
    scheduleId: typeof t.scheduleId === "string" ? t.scheduleId : kind === "scheduled" ? ((t.sourceRef as string | null) ?? "scheduled") : null,
    contextTokens: typeof t.contextTokens === "number" ? t.contextTokens : 0,
  };
}

export function contextWindowOf(t: Pick<ChatThread, "contextWindow"> | null | undefined): number {
  return t?.contextWindow && t.contextWindow > 0 ? t.contextWindow : DEFAULT_CONTEXT_WINDOW;
}

export async function listThreads(signal?: AbortSignal, brandId: string | null = lastBrand()): Promise<ChatThread[]> {
  const raw = pickArray<unknown>(await api(chatBrandPath("/api/chat/threads", brandId), { signal }), "threads");
  return raw.map(normalizeThread).filter((t): t is ChatThread => !!t && t.brandId === brandId);
}

export interface CreateThreadBody {
  brandId?: string | null;
  title?: string;
  autonomy?: Autonomy;
  mode?: ChatMode;
  thinkingLevel?: ThinkingLevel;
  /** "Ask about this" snapshot — stored as the thread's first (context) message. */
  context?: Record<string, unknown> | null;
}

export async function createThread(body: CreateThreadBody = {}): Promise<ChatThread> {
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) if (v !== undefined && v !== null && v !== "") clean[k] = v;
  const res = await api("/api/chat/threads", { method: "POST", body: clean });
  const t = normalizeThread(pickObject(res, "thread"));
  if (!t?.id) throw new ApiError(500, "bad_response", "Server did not return a thread");
  return t;
}

export async function getThread(id: string, signal?: AbortSignal, brandId: string | null = lastBrand()): Promise<{ thread: ChatThread | null; messages: ChatMessage[] }> {
  const res = await api<Record<string, unknown>>(chatBrandPath(`/api/chat/threads/${encodeURIComponent(id)}`, brandId), { signal });
  const thread = normalizeThread(res && typeof res.thread === "object" ? res.thread : res);
  if (thread?.brandId !== brandId) throw new ApiError(403, "forbidden", "This chat is not in the selected brand.");
  const messages = pickArray<ChatMessage>(res, "messages").map(normalizeMessage);
  messages.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0) || (Date.parse(a.createdAt ?? "") || 0) - (Date.parse(b.createdAt ?? "") || 0));
  return { thread, messages };
}

/** PATCH title/autonomy/model. Resolves `null` when the server doesn't support it yet (404/405) so callers keep it locally. */
export async function updateThread(id: string, patch: { title?: string; autonomy?: Autonomy; mode?: ChatMode; thinkingLevel?: ThinkingLevel }): Promise<ChatThread | null> {
  try {
    const res = await api(chatBrandPath(`/api/chat/threads/${encodeURIComponent(id)}`), { method: "PATCH", body: patch });
    return normalizeThread(pickObject(res, "thread"));
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 405 || e.status === 501)) return null;
    throw e;
  }
}

export async function markThreadRead(id: string): Promise<void> {
  try {
    await api(chatBrandPath(`/api/chat/threads/${encodeURIComponent(id)}/read`), { method: "POST", body: {}, noAuthRedirect: true });
  } catch {
    /* best effort */
  }
}

export async function stopThread(id: string): Promise<void> {
  await api(chatBrandPath(`/api/chat/threads/${encodeURIComponent(id)}/stop`), { method: "POST", body: {} });
}

export function normalizeMessage(m: ChatMessage): ChatMessage {
  const raw = m.content as unknown;
  const content: ContentBlock[] = Array.isArray(raw) ? (raw as ContentBlock[]) : typeof raw === "string" ? [{ type: "text", text: raw }] : [];
  return { ...m, content, attachments: Array.isArray(m.attachments) ? m.attachments : [], meta: m.meta ?? {} };
}

// ─── Stream events ─────────────────────────────────────────────────────────

export type StreamEvent =
  | { type: "text"; delta: string }
  | { type: "thinking"; delta?: string }
  | { type: "status"; status: string; label?: string }
  | { type: "tool_use"; id: string; name: string; input: unknown }
  | { type: "tool_result"; id: string; result?: unknown; error?: string }
  | { type: "attachment"; attachment: ChatAttachment }
  | { type: "question"; attachment: ChatAttachment }
  | { type: "message"; message: ChatMessage }
  | { type: "usage"; inputTokens?: number; outputTokens?: number; contextPct?: number; contextTokens?: number; contextWindow?: number }
  | { type: "interjection_queued"; id?: string; text?: string }
  | { type: "interjection_applied"; ids: string[] }
  | { type: "stopped" }
  | { type: "done" }
  | { type: "error"; message: string };

const numOrUndef = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

export function toStreamEvent(name: string | undefined, data: unknown): StreamEvent | null {
  const d = (data && typeof data === "object" && !Array.isArray(data) ? data : { text: data }) as Record<string, unknown>;
  const type = (name && name !== "message" ? name : (d.type as string | undefined)) ?? name ?? "message";
  switch (type) {
    case "text":
    case "delta":
    case "text_delta":
      return { type: "text", delta: String(d.delta ?? d.text ?? "") };
    case "thinking":
    case "thinking_delta":
      return { type: "thinking", delta: typeof (d.delta ?? d.text) === "string" ? String(d.delta ?? d.text) : undefined };
    case "status":
      return { type: "status", status: String(d.status ?? d.state ?? ""), label: typeof d.label === "string" && d.label ? d.label : undefined };
    case "tool_use":
    case "tool_call":
      return { type: "tool_use", id: String(d.id ?? d.tool_use_id ?? d.toolUseId ?? ""), name: String(d.name ?? d.tool ?? ""), input: d.input };
    case "tool_result": {
      const isErr = d.is_error === true || d.isError === true || d.ok === false;
      const body = d.result ?? d.content ?? d.output ?? d.preview;
      const error = typeof d.error === "string" ? d.error : isErr ? (typeof body === "string" ? body : typeof d.message === "string" ? d.message : "Tool failed") : undefined;
      return { type: "tool_result", id: String(d.id ?? d.tool_use_id ?? d.toolUseId ?? ""), result: body, error };
    }
    case "attachment":
      return { type: "attachment", attachment: (d.attachment && typeof d.attachment === "object" ? d.attachment : d) as ChatAttachment };
    case "question": {
      if (d.attachment && typeof d.attachment === "object") return { type: "question", attachment: d.attachment as ChatAttachment };
      const { type: _t, attachmentId, id, ...rest } = d;
      return { type: "question", attachment: { kind: "question", id: String(attachmentId ?? id ?? ""), data: rest } };
    }
    case "message":
      if (d.message && typeof d.message === "object") return { type: "message", message: normalizeMessage(d.message as ChatMessage) };
      if (d.role && d.content) return { type: "message", message: normalizeMessage(d as unknown as ChatMessage) };
      return null;
    case "usage":
      return {
        type: "usage",
        inputTokens: numOrUndef(d.inputTokens ?? d.input_tokens),
        outputTokens: numOrUndef(d.outputTokens ?? d.output_tokens),
        contextPct: numOrUndef(d.contextPct ?? d.pctFull),
        contextTokens: numOrUndef(d.contextTokens),
        contextWindow: numOrUndef(d.contextWindow ?? d.contextBudget),
      };
    case "interjection_queued":
      return { type: "interjection_queued", id: typeof (d.inputId ?? d.id) === "string" ? String(d.inputId ?? d.id) : undefined, text: typeof d.text === "string" ? d.text : undefined };
    case "interjection_applied": {
      const ids: string[] = [];
      if (typeof d.id === "string") ids.push(d.id);
      for (const k of ["ids", "inputIds"]) if (Array.isArray(d[k])) for (const x of d[k] as unknown[]) if (typeof x === "string") ids.push(x);
      if (Array.isArray(d.inputs)) for (const x of d.inputs) if (x && typeof (x as { id?: unknown }).id === "string") ids.push((x as { id: string }).id);
      return { type: "interjection_applied", ids };
    }
    case "stopped":
      return { type: "stopped" };
    case "done":
    case "end":
      return { type: "done" };
    case "error":
      return { type: "error", message: String(d.message ?? d.error ?? "Stream error") };
    default:
      return null;
  }
}

/** Incremental SSE parser (handles multi-line data and split chunks). */
export function createSseParser(onEvent: (name: string | undefined, data: string) => void) {
  let buffer = "";
  return (chunk: string) => {
    buffer += chunk.replace(/\r\n/g, "\n");
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let name: string | undefined;
      const data: string[] = [];
      for (const line of raw.split("\n")) {
        if (line.startsWith(":")) continue;
        if (line.startsWith("event:")) name = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
      }
      if (data.length) onEvent(name, data.join("\n"));
    }
  };
}

export type ThinkingLevel = "quick" | "standard" | "thorough" | "comprehensive";
export type ChatMode = "instant" | "thinking";

/** Server `composer` schema sent with each message. */
export interface ComposerSettings {
  mode?: ChatMode;
  thinkingLevel?: ThinkingLevel;
  autonomy?: Autonomy;
  imageModel?: string;
  videoModel?: string;
  videoResolution?: string;
}

/** Composer attachment (server resolves the media from ids/storage keys; `url` is informational). */
export interface OutgoingAttachment {
  kind: "upload" | "asset" | "generated" | "creation" | "inspiration";
  id?: string;
  storageKey?: string;
  name?: string;
  url?: string;
  mediaType?: "image" | "video";
}

export interface SendMessageBody {
  content: string;
  composer?: ComposerSettings;
  attachments?: OutgoingAttachment[];
}

export interface ChatRequestResult {
  /** True when the response was an event stream that was consumed. */
  streamed: boolean;
  /** JSON body of a non-streaming response. */
  json?: unknown;
}

/**
 * POST to a chat endpoint that may answer with an event stream or JSON. With `consume: false` an event
 * stream is cancelled immediately (used for interjections while this tab already holds the run's stream).
 */
export async function chatRequest(
  path: string,
  body: unknown,
  opts: { onEvent?: (e: StreamEvent) => void; signal?: AbortSignal; consume?: boolean; method?: "GET" | "POST" } = {},
): Promise<ChatRequestResult> {
  let res: Response;
  const method = opts.method ?? "POST";
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: method === "GET" ? { accept: "text/event-stream" } : { "content-type": "application/json", accept: "text/event-stream, application/json" },
      body: method === "GET" ? undefined : JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return { streamed: false };
    throw new ApiError(0, "network", "Can't reach the Mimic server. Is it running?");
  }
  if (!res.ok) {
    let msg = res.statusText;
    let code = `http_${res.status}`;
    let details: Record<string, unknown> = {};
    const text = await res.text().catch(() => "");
    try {
      const j = text ? JSON.parse(text) : {};
      msg = j.message ?? msg;
      code = j.error ?? code;
      details = j.details ?? {};
    } catch {
      if (text) msg = text.slice(0, 300);
    }
    if ((res.status === 502 || res.status === 504 || res.status === 500) && /ECONNREFUSED|proxy|socket hang up|^$/i.test(text)) {
      throw new ApiError(res.status, "network", "Can't reach the Mimic server. Is it running?");
    }
    throw new ApiError(res.status, code, msg || "Request failed", details);
  }
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("event-stream") || !res.body) {
    const json = res.status === 204 ? null : await res.json().catch(() => null);
    return { streamed: false, json };
  }
  const reader = res.body.getReader();
  if (opts.consume === false) {
    void reader.cancel().catch(() => {});
    return { streamed: false };
  }
  const decoder = new TextDecoder();
  const parse = createSseParser((name, data) => {
    let parsed: unknown = data;
    try {
      parsed = JSON.parse(data);
    } catch {
      /* plain text delta */
    }
    const ev = toStreamEvent(name, parsed);
    if (ev) opts.onEvent?.(ev);
  });
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      parse(decoder.decode(value, { stream: true }));
    }
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return { streamed: true };
    throw new ApiError(0, "network", "The connection to Mimic dropped mid-reply.");
  }
  parse("\n\n");
  return { streamed: true };
}

/** POST a user message and stream the agent's reply. Resolves when the stream ends (always emits a final `done`). */
export async function streamMessage(threadId: string, body: SendMessageBody, onEvent: (e: StreamEvent) => void, signal?: AbortSignal): Promise<void> {
  const r = await chatRequest(messagesPath(threadId), body, { onEvent, signal });
  if (!r.streamed && r.json) {
    const m = pickObject<ChatMessage>(r.json, "message");
    if (m?.content) onEvent({ type: "message", message: normalizeMessage(m) });
  }
  onEvent({ type: "done" });
}

export type AnswerFile = { storageKey: string } | { assetId: string } | { creativeId: string };

export interface AnswerItem {
  questionId: string;
  optionIds?: string[];
  text?: string;
  /** Upload questions: exactly one of storageKey (POST /api/uploads) | assetId | creativeId per file. */
  files?: AnswerFile[];
  skipped?: boolean;
}

/** 👍 / 👎 on an agent message. */
export async function sendMessageFeedback(messageId: string, rating: "up" | "down", note?: string): Promise<void> {
  await api(chatBrandPath(`/api/chat/messages/${encodeURIComponent(messageId)}/feedback`), { method: "POST", body: { rating, ...(note ? { note } : {}) } });
}

export function eventsPath(threadId: string): string {
  return chatBrandPath(`/api/chat/threads/${encodeURIComponent(threadId)}/events`);
}

// ─── Composer templates / agent persona (GET /api/chat/templates) ──────────

export interface ServerTemplateGroup {
  id: string;
  label: string;
  color?: string;
  templates: { id?: string; title: string; description?: string; prompt: string; recurring?: boolean }[];
}

export interface ChatTemplatesInfo {
  groups: ServerTemplateGroup[];
  contextBudget?: number;
  agentName?: string | null;
}

export async function getChatTemplates(signal?: AbortSignal): Promise<ChatTemplatesInfo> {
  const res = await api<Record<string, unknown>>("/api/chat/templates", { signal });
  const groups = pickArray<ServerTemplateGroup>(res, "groups").filter((g) => g && typeof g.id === "string" && Array.isArray(g.templates));
  return {
    groups,
    contextBudget: typeof res?.contextBudget === "number" ? res.contextBudget : undefined,
    agentName: typeof res?.agentName === "string" ? res.agentName : null,
  };
}

export function answersPath(threadId: string): string {
  return chatBrandPath(`/api/chat/threads/${encodeURIComponent(threadId)}/answers`);
}

export function messagesPath(threadId: string): string {
  return chatBrandPath(`/api/chat/threads/${encodeURIComponent(threadId)}/messages`);
}

// ─── Reports (artifacts) ───────────────────────────────────────────────────

/** Mirrors `ArtifactSection` in packages/core/src/db/schema/artifacts.ts (kept local so the web build doesn't type-check drizzle). */
export type ArtifactSection =
  | { kind: "summary"; label: string; markdown: string }
  | { kind: "callout"; tone: "positive" | "negative" | "neutral"; markdown: string }
  | {
      kind: "kpis";
      items: {
        label: string;
        value: number | null;
        format: "currency" | "number" | "percent";
        delta?: number | null;
        deltaPct?: number | null;
        better?: "up" | "down";
        comparison: string;
        spark?: number[];
      }[];
    }
  | { kind: "table"; title: string; columns: { key: string; label: string; format?: string }[]; rows: Record<string, unknown>[]; comparisonLabel?: string }
  | { kind: "chart"; title: string; chart: "line" | "bar" | "dual_axis" | "donut" | "bubble" | "funnel"; series: { key: string; label: string; axis?: "left" | "right" }[]; data: Record<string, unknown>[] }
  | {
      kind: "recommendations";
      title?: string;
      items: { target: string; action: string; success: string; guardrail: string; reviewDate: string; recommendationId?: string; tool?: { name: string; input: Record<string, unknown> } }[];
    }
  | { kind: "markdown"; title?: string; markdown: string };

export interface Artifact {
  id: string;
  brandId?: string;
  threadId?: string | null;
  documentId?: string;
  revision?: number;
  kind?: string;
  title: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  comparison?: string | null;
  sections: ArtifactSection[];
  limitations: string[];
  changesMade?: string | null;
  createdBy?: string | null;
  publishedAt?: string | null;
  revisions?: { id: string; revision: number; publishedAt: string | null }[];
}

export function normalizeArtifact(raw: unknown): Artifact | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  if (typeof a.id !== "string") return null;
  return {
    ...(a as unknown as Artifact),
    title: typeof a.title === "string" && a.title ? a.title : "Report",
    sections: Array.isArray(a.sections) ? (a.sections as ArtifactSection[]) : [],
    limitations: Array.isArray(a.limitations) ? (a.limitations as unknown[]).filter((x): x is string => typeof x === "string") : [],
    publishedAt: isoOrNull(a.publishedAt),
  };
}

/** GET /api/artifacts/:id → { artifact, revisions }; falls back to `reports.get { brandId, artifactId }` when the route is missing. */
export async function getArtifact(id: string, opts: { brandId?: string | null; signal?: AbortSignal } = {}): Promise<Artifact> {
  const { signal, brandId } = opts;
  const viaTool = async (original: unknown) => {
    if (!brandId) throw original ?? new ApiError(404, "not_found", "Report not found");
    try {
      return await callTool("reports.get", { brandId, artifactId: id }, { signal });
    } catch (e2) {
      if (e2 instanceof ApiError && e2.isUnknownTool && original) throw original;
      throw e2;
    }
  };
  let res: unknown;
  try {
    res = await api(`/api/artifacts/${encodeURIComponent(id)}`, { signal });
  } catch (e) {
    if (!(e instanceof ApiError) || !(e.status === 405 || e.status === 501 || (e.status === 404 && !/artifact|report/i.test(e.message)))) throw e;
    res = await viaTool(e);
  }
  // An SPA fallback (HTML) means the route isn't mounted.
  if (typeof res === "string") res = await viaTool(null);
  const a = normalizeArtifact(pickObject(res, "artifact", "report"));
  if (!a) throw new ApiError(404, "not_found", "Report not found");
  const revs = res && typeof res === "object" ? ((res as Record<string, unknown>).revisions ?? (pickObject(res, "artifact", "report") as Record<string, unknown> | null)?.revisions) : undefined;
  if (Array.isArray(revs)) {
    a.revisions = revs
      .filter((r): r is { id: string; revision: number; publishedAt?: string } => !!r && typeof (r as { id?: unknown }).id === "string")
      .map((r) => ({ id: r.id, revision: Number(r.revision) || 1, publishedAt: isoOrNull(r.publishedAt) }))
      .sort((x, y) => x.revision - y.revision);
  }
  return a;
}

/** Other report documents of the brand (latest revision per document) — `reports.list { brandId }` → { items }. */
export async function listArtifacts(brandId: string, signal?: AbortSignal): Promise<Artifact[]> {
  const res = await callTool("reports.list", { brandId, limit: 100 }, { signal });
  return pickArray<unknown>(res, "items", "artifacts", "reports")
    .map((r) => normalizeArtifact({ sections: [], limitations: [], ...(r as Record<string, unknown>) }))
    .filter((a): a is Artifact => !!a);
}

export function artifactDownloadUrl(id: string, format: "json" | "md", revision?: number): string {
  const q = new URLSearchParams();
  if (format === "json") q.set("format", "json");
  if (revision) q.set("revision", String(revision));
  const qs = q.toString();
  return `/api/artifacts/${encodeURIComponent(id)}/download${qs ? `?${qs}` : ""}`;
}

/** POST /api/artifacts/:id/share → { url, expiresAt } (signed, read-only link). */
export async function shareArtifact(id: string, expiresInHours = 168): Promise<{ url: string; expiresAt?: string }> {
  const res = await api<{ url?: string; expiresAt?: string }>(`/api/artifacts/${encodeURIComponent(id)}/share`, { method: "POST", body: { expiresInHours } });
  if (!res?.url) throw new ApiError(500, "bad_response", "No share link returned");
  return { url: res.url, expiresAt: res.expiresAt };
}

// ─── Models (composer chips) ───────────────────────────────────────────────

export interface GenModel {
  id: string;
  label: string;
  kind: "image" | "edit" | "video" | string;
  description?: string;
  resolutions?: string[];
  defaultResolution?: string;
  tags?: string[];
  supportsGenerate?: boolean;
}

export function normalizeModels(raw: unknown): GenModel[] {
  let list = pickArray<Record<string, unknown>>(raw, "models", "catalog");
  if (!list.length && raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    // creatives.models → { defaults: {image, video}, image: [...], edit: [...], video: [...] }
    const defaults = (o.defaults && typeof o.defaults === "object" ? o.defaults : {}) as Record<string, unknown>;
    for (const kind of ["image", "video"]) {
      if (!Array.isArray(o[kind])) continue;
      for (const m of o[kind] as Record<string, unknown>[]) {
        if (!m || typeof m !== "object") continue;
        const tags = Array.isArray(m.tags) ? [...(m.tags as string[])] : [];
        if (defaults[kind] === m.id) tags.push("default");
        list.push({ kind, ...m, tags });
      }
    }
  }
  list = list.filter(Boolean);
  return list
    .filter((m) => m && typeof m.id === "string")
    .map((m) => ({
      id: m.id as string,
      label: (typeof m.label === "string" && m.label) || (typeof m.name === "string" && m.name) || (m.id as string),
      kind: (typeof m.kind === "string" && m.kind) || (typeof m.type === "string" && m.type) || "image",
      description: typeof m.description === "string" ? m.description : undefined,
      resolutions: Array.isArray(m.resolutions) ? (m.resolutions as unknown[]).filter((r): r is string => typeof r === "string") : undefined,
      defaultResolution: typeof m.defaultResolution === "string" ? m.defaultResolution : undefined,
      tags: Array.isArray(m.tags) ? (m.tags as unknown[]).filter((r): r is string => typeof r === "string") : undefined,
      supportsGenerate: typeof m.supportsGenerate === "boolean" ? m.supportsGenerate : undefined,
    }))
    .filter((m) => m.supportsGenerate !== false);
}
